<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useConfig } from 'dashboard/composables/useConfig';
import { useAccount } from 'dashboard/composables/useAccount';
import { useUISettings } from 'dashboard/composables/useUISettings';

defineProps({
  label: { type: String, default: '' },
  description: { type: String, default: '' },
});

const { t, locale } = useI18n();
const { updateUISettings, uiSettings } = useUISettings();
const { enabledLanguages } = useConfig();
const { currentAccount } = useAccount();
const showLanguageModal = ref(false);
const currentLanguage = computed(() => uiSettings.value?.locale ?? '');

const languageOptions = computed(() => [
  {
    name: t(
      'PROFILE_SETTINGS.FORM.INTERFACE_SECTION.LANGUAGE.USE_ACCOUNT_DEFAULT'
    ),
    iso_639_1_code: '',
  },
  ...(enabledLanguages ?? []),
]);

const updateLanguage = async languageCode => {
  try {
    if (!languageCode) {
      // Clear preference to use account default
      await updateUISettings({ locale: null });
      locale.value = currentAccount.value.locale;
      useAlert(
        t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.LANGUAGE.UPDATE_SUCCESS')
      );
      return;
    }

    const valid = (enabledLanguages || []).some(
      l => l.iso_639_1_code === languageCode
    );
    if (!valid) {
      throw new Error(`Invalid language code: ${languageCode}`);
    }

    await updateUISettings({ locale: languageCode });
    // Apply immediately if the user explicitly chose a preference
    locale.value = languageCode;

    useAlert(
      t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.LANGUAGE.UPDATE_SUCCESS')
    );
  } catch (error) {
    useAlert(
      t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.LANGUAGE.UPDATE_ERROR')
    );
    throw error;
  }
};

const selectedValue = computed({
  get: () => currentLanguage.value,
  set: value => {
    updateLanguage(value);
  },
});

const selectedLanguageLabel = computed(() => {
  if (!selectedValue.value) {
    return t(
      'PROFILE_SETTINGS.FORM.INTERFACE_SECTION.LANGUAGE.USE_ACCOUNT_DEFAULT'
    );
  }
  const language =
    languageOptions.value.find(
      lang => lang.iso_639_1_code === selectedValue.value
    ) || {};
  return language.name;
});

const openLanguageModal = () => {
  showLanguageModal.value = true;
};

const closeLanguageModal = () => {
  showLanguageModal.value = false;
};

const handleLanguageSelect = async code => {
  await updateLanguage(code);
  closeLanguageModal();
};
</script>

<template>
  <div class="flex gap-2 justify-between w-full items-start">
    <div>
      <label class="text-n-gray-12 font-medium leading-6 text-sm">
        {{ label }}
      </label>
      <p class="text-n-gray-11">
        {{ description }}
      </p>
    </div>
    <button
      type="button"
      class="min-w-28 mt-px px-3 py-2 text-left border rounded-lg flex items-center justify-between gap-2 bg-n-solid-1 text-n-strong border-n-weak"
      @click="openLanguageModal"
    >
      <span>{{ selectedLanguageLabel }}</span>
      <span class="i-lucide-chevron-down text-n-slate-11" />
    </button>
  </div>
  <woot-modal
    v-model:show="showLanguageModal"
    size="small"
    :on-close="closeLanguageModal"
  >
    <woot-modal-header
      :header-title="
        t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.LANGUAGE.MODAL_TITLE')
      "
    />
    <section class="p-6 pt-0">
      <div class="flex flex-col gap-2 max-h-80 overflow-y-auto">
        <button
          v-for="option in languageOptions"
          :key="option.iso_639_1_code || 'default'"
          type="button"
          class="px-4 py-2 text-left border rounded-lg hover:bg-n-alpha-1 focus:bg-n-alpha-1 border-n-weak"
          @click="handleLanguageSelect(option.iso_639_1_code)"
        >
          <span
            class="font-medium"
            :class="{
              'text-n-brand': option.iso_639_1_code === selectedValue,
            }"
          >
            {{ option.name }}
          </span>
        </button>
      </div>
      <div class="flex justify-end mt-4">
        <button class="text-sm text-n-brand" @click="closeLanguageModal">
          {{ t('GENERAL_SETTINGS.CANCEL') }}
        </button>
      </div>
    </section>
  </woot-modal>
</template>
